/**
 * KYC Debug Helper - Utility functions for debugging KYC status issues
 */

export const KYCDebugHelper = {
  /**
   * Log all KYC-related localStorage data
   */
  logAllKYCData: () => {
    console.group('🔍 KYC Debug - All Storage Data');
    
    // Check all localStorage keys
    const allKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('kyc') || key.includes('KYC') || key.includes('merchant'))) {
        allKeys.push(key);
      }
    }
    
    console.log('📋 KYC-related keys found:', allKeys);
    
    // Log each key's data
    allKeys.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        const parsed = JSON.parse(data);
        console.log(`🔑 ${key}:`, parsed);
      } catch (e) {
        console.log(`🔑 ${key}:`, localStorage.getItem(key));
      }
    });
    
    console.groupEnd();
  },

  /**
   * Check KYC status for a specific merchant
   */
  checkMerchantStatus: (merchantId) => {
    console.group(`🏪 KYC Debug - Merchant ${merchantId}`);
    
    const keys = [
      'kycStatus',
      'kycVerificationComplete',
      `kyc_status_${merchantId}`,
      `merchant_kyc_${merchantId}`,
      `merchant_docs_${merchantId}`,
      `merchant_verification_${merchantId}`,
      `kyc_documents_${merchantId}`
    ];
    
    keys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        try {
          const parsed = JSON.parse(data);
          console.log(`✅ ${key}:`, parsed);
        } catch (e) {
          console.log(`✅ ${key}:`, data);
        }
      } else {
        console.log(`❌ ${key}: Not found`);
      }
    });
    
    console.groupEnd();
  },

  /**
   * Clear all KYC data for debugging
   */
  clearAllKYCData: () => {
    console.log('🧹 Clearing all KYC data...');
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('kyc') || key.includes('KYC') || key.includes('merchant'))) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed: ${key}`);
    });
    
    console.log(`✅ Cleared ${keysToRemove.length} KYC-related items`);
  },

  /**
   * Create test KYC data
   */
  createTestData: (merchantId = 'test-merchant') => {
    console.log(`🧪 Creating test KYC data for merchant: ${merchantId}`);
    
    const testStatus = {
      status: 'APPROVED',
      fullName: 'Test Merchant',
      businessName: 'Test Business Ltd',
      panVerified: true,
      gstinVerified: true,
      registeredAddress: '123 Test Street',
      city: 'Test City',
      state: 'Test State',
      bankVerified: true,
      documentsUploaded: true,
      submittedAt: new Date().toISOString(),
      completionPercentage: 100,
      verificationResults: {
        status: 'APPROVED',
        finalScore: 95,
        stepScores: {
          authenticity_check: 95,
          data_extraction: 90,
          face_verification: 100,
          consistency_check: 95
        }
      },
      lastUpdated: new Date().toISOString(),
      merchantId: merchantId
    };
    
    // Save in multiple formats
    localStorage.setItem('kycStatus', JSON.stringify(testStatus));
    localStorage.setItem(`kyc_status_${merchantId}`, JSON.stringify(testStatus));
    localStorage.setItem('kycVerificationComplete', 'true');
    
    console.log('✅ Test data created successfully');
    return testStatus;
  },

  /**
   * Validate KYC data consistency
   */
  validateDataConsistency: (merchantId) => {
    console.group(`🔍 KYC Data Validation - Merchant ${merchantId}`);
    
    const generalStatus = localStorage.getItem('kycStatus');
    const merchantStatus = localStorage.getItem(`kyc_status_${merchantId}`);
    const completionFlag = localStorage.getItem('kycVerificationComplete');
    
    let issues = [];
    
    if (!generalStatus && !merchantStatus) {
      issues.push('❌ No KYC status found in any storage location');
    }
    
    if (generalStatus && merchantStatus) {
      try {
        const general = JSON.parse(generalStatus);
        const merchant = JSON.parse(merchantStatus);
        
        if (general.status !== merchant.status) {
          issues.push(`⚠️ Status mismatch: general(${general.status}) vs merchant(${merchant.status})`);
        }
        
        if (general.completionPercentage !== merchant.completionPercentage) {
          issues.push(`⚠️ Completion mismatch: general(${general.completionPercentage}%) vs merchant(${merchant.completionPercentage}%)`);
        }
      } catch (e) {
        issues.push('❌ Failed to parse status data');
      }
    }
    
    if ((generalStatus || merchantStatus) && !completionFlag) {
      issues.push('⚠️ KYC status exists but completion flag is missing');
    }
    
    if (issues.length === 0) {
      console.log('✅ All KYC data is consistent');
    } else {
      console.log('Issues found:');
      issues.forEach(issue => console.log(issue));
    }
    
    console.groupEnd();
    return issues.length === 0;
  }
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.KYCDebugHelper = KYCDebugHelper;
}

export default KYCDebugHelper;